/**
 * Token Limits Service
 * 
 * API client for managing and monitoring token usage limits.
 */

import { authenticatedFetch } from '../utils/authApi';

export interface TokenLimit {
  id: string;
  limit_type: 'user' | 'group' | 'organization';
  entity_id: string;
  display_name: string;
  max_tokens: number;
  used_tokens: number;
  remaining_tokens: number;
  percent_used: number;
  is_blocked: boolean;
  reset_period: 'daily' | 'weekly' | 'monthly' | 'none';
  last_reset: string;
  notify_at_percent: number[];
  block_at_percent: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TokenLimitsDashboard {
  summary: {
    total_limits: number;
    total_allocated_tokens: number;
    total_used_tokens: number;
    overall_percent_used: number;
    blocked_count: number;
    by_type_count: {
      user: number;
      group: number;
      organization: number;
    };
  };
  limits: {
    user: TokenLimit[];
    group: TokenLimit[];
    organization: TokenLimit[];
  };
}

export interface UserUsageSummary {
  user_email: string;
  user: TokenLimit | null;
  groups: TokenLimit[];
  organization: TokenLimit | null;
  has_any_limit: boolean;
  is_blocked: boolean;
  blocked_reason: string | null;
}

export interface OrganizationUsage {
  has_limit: boolean;
  organization: {
    organization_id: string;
    display_name: string;
    max_tokens: number;
    used_tokens: number;
    remaining_tokens: number;
    percent_used: number;
    is_blocked: boolean;
    reset_period: string;
    last_reset: string;
  } | null;
}

class TokenLimitsService {
  private baseUrl = '/api/admin/token-limits';
  private userBaseUrl = '/api/token-limits';

  // =========================================================================
  // Admin Operations
  // =========================================================================

  async getDashboard(): Promise<TokenLimitsDashboard> {
    const response = await authenticatedFetch(`${this.baseUrl}/dashboard`);
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get token limits dashboard');
    }
    return data;
  }

  async getAllLimits(limitType?: string): Promise<TokenLimit[]> {
    const url = limitType 
      ? `${this.baseUrl}?limit_type=${limitType}`
      : this.baseUrl;
    const response = await authenticatedFetch(url);
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get token limits');
    }
    return data.limits;
  }

  async getLimit(limitType: string, entityId: string): Promise<TokenLimit> {
    const response = await authenticatedFetch(
      `${this.baseUrl}/${limitType}/${encodeURIComponent(entityId)}`
    );
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get token limit');
    }
    return data.limit;
  }

  async createLimit(limit: {
    limit_type: string;
    entity_id: string;
    display_name?: string;
    max_tokens: number;
    reset_period?: string;
    notify_at_percent?: number[];
    block_at_percent?: number;
  }): Promise<TokenLimit> {
    const response = await authenticatedFetch(this.baseUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(limit),
    });
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to create token limit');
    }
    return data.limit;
  }

  async updateLimit(
    limitType: string,
    entityId: string,
    updates: Partial<TokenLimit>
  ): Promise<TokenLimit> {
    const response = await authenticatedFetch(
      `${this.baseUrl}/${limitType}/${encodeURIComponent(entityId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      }
    );
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to update token limit');
    }
    return data.limit;
  }

  async deleteLimit(limitType: string, entityId: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.baseUrl}/${limitType}/${encodeURIComponent(entityId)}`,
      { method: 'DELETE' }
    );
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to delete token limit');
    }
  }

  async resetUsage(limitType: string, entityId: string): Promise<TokenLimit> {
    const response = await authenticatedFetch(
      `${this.baseUrl}/${limitType}/${encodeURIComponent(entityId)}/reset`,
      { method: 'POST' }
    );
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to reset usage');
    }
    return data.limit;
  }

  // =========================================================================
  // User Operations (for current user)
  // =========================================================================

  async getMyUsage(): Promise<UserUsageSummary> {
    const response = await authenticatedFetch(`${this.userBaseUrl}/my-usage`);
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get usage');
    }
    return data.usage;
  }

  async checkCanUse(tokens?: number): Promise<{
    allowed: boolean;
    blocked_by: string | null;
    blocked_reason: string | null;
    usage: any;
  }> {
    const url = tokens 
      ? `${this.userBaseUrl}/can-use?tokens=${tokens}`
      : `${this.userBaseUrl}/can-use`;
    const response = await authenticatedFetch(url);
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to check usage');
    }
    return data;
  }

  async getOrganizationUsage(): Promise<OrganizationUsage> {
    const response = await authenticatedFetch(`${this.userBaseUrl}/organization`);
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get organization usage');
    }
    return data;
  }
}

export const tokenLimitsService = new TokenLimitsService();
